/**
 * tourSteps/inboxSteps.js
 * Comprehensive tour steps for the Project Management Action Inbox & Activity Control Center.
 * Guides through unread counts, bulk actions, AI summaries, period selectors,
 * executive modals, all 6 category tabs (All, Action Required, Activity, @Mentioned, Bookmarks, Archive),
 * filter controls, mentions scope, and timeline actions.
 */

export const inboxSteps = [
    {
        title: 'Action Inbox & Communications Center',
        description: 'Your centralized notification center for all project communications, review requests, client feedback, and real-time status alerts across the ERP ecosystem.',
        selector: '[data-tour="inbox-header"]',
        placement: 'bottomLeft',
    },
    {
        title: 'Instant "Mark All as Read"',
        description: 'Clear your unread notifications with one click. Immediately syncs unread counters across all client sessions via WebSockets.',
        selector: '[data-tour="inbox-mark-all-read"]',
        fallbackSelector: '[data-tour="inbox-header"]',
        placement: 'bottom',
    },
    {
        title: 'AI Inbox Executive Summary',
        description: 'AI continuously synthesizes all unread notifications, review requests, and blocked tasks into high-priority actionable takeaways with direct jump links.',
        selector: '[data-tour="inbox-ai-summary"]',
        placement: 'bottom',
    },
    {
        title: 'Summary Timeframe & Live Refresh',
        description: 'Switch AI synthesis timeframes between Today, Past 3 Days, Past Week, or Custom Date Ranges. Click the refresh button to re-run AI synthesis with the latest workspace activity.',
        selector: '[data-tour="inbox-period-selector"]',
        fallbackSelector: '[data-tour="inbox-ai-summary"]',
        placement: 'bottom',
    },
    {
        title: 'Full Executive Briefing Modal',
        description: 'Open the deep-dive AI briefing modal featuring 5 executive sections: Team Sentiment, Critical Delivery Blockers, Urgent Actions, Strategic Alignment, and Milestone Pace.',
        selector: '[data-tour="inbox-full-summary-btn"]',
        fallbackSelector: '[data-tour="inbox-ai-summary"]',
        placement: 'bottomRight',
    },
    {
        title: 'Workspace Category Navigation',
        description: 'Categorizes incoming communications into 6 purpose-built operational streams so team members can prioritize urgent deliverables and filter out background noise.',
        selector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Tab 1: All Notifications',
        description: 'The master chronological feed combining all updates, comments, stage transitions, and alerts across every project you have access to.',
        selector: '[data-tour="inbox-tab-all"]',
        fallbackSelector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Tab 2: Action Required Stream',
        description: 'Crucial pending items demanding your direct intervention — client review requests, deliverable sign-offs, revision feedback notes, and blocked dependencies.',
        selector: '[data-tour="inbox-tab-action"]',
        fallbackSelector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Tab 3: Workspace Activity Audit Log',
        description: 'Continuous operational audit trail recording team progress, task creation, stage transitions, file uploads, and milestone achievements in real time.',
        selector: '[data-tour="inbox-tab-activity"]',
        fallbackSelector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Tab 4: @Mentioned Discussions',
        description: 'Direct communication stream filtering strictly for discussion comments and task notes where your username was explicitly tagged with "@". Never miss teammate conversations or client queries.',
        selector: '[data-tour="inbox-tab-mentions"]',
        fallbackSelector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Tab 5: Starred Bookmarks',
        description: 'Your personal high-priority follow-up list. Click the bookmark icon on any notification card to pin it here for deferred action without losing track in your main feed.',
        selector: '[data-tour="inbox-tab-bookmarks"]',
        fallbackSelector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Tab 6: Notification Archive',
        description: 'Historical archive of resolved and dismissed notifications. Items are safely preserved here for audit compliance and can be searched or unarchived at any time.',
        selector: '[data-tour="inbox-tab-archive"]',
        fallbackSelector: '[data-tour="inbox-workspace-tabs"]',
        placement: 'bottom',
    },
    {
        title: 'Search, Project & Priority Controls',
        description: 'Refine your stream with granular filters: narrow by project, filter by urgency priority level (Critical, High, Medium, Normal), or full-text keyword search across titles and team members.',
        selector: '[data-tour="inbox-controls-bar"]',
        placement: 'bottom',
    },
    {
        title: 'Mentions Scope Switcher (Admin/Manager)',
        description: 'Managers and admins can toggle between "My Mentions" (personally tagged comments) and "All Team Mentions" to oversee cross-team discussions and proactively resolve bottlenecks.',
        selector: '[data-tour="inbox-mentions-scope"]',
        fallbackSelector: '[data-tour="inbox-controls-bar"]',
        placement: 'bottom',
    },
    {
        title: 'Chronological Timeline Stream & Actions',
        description: 'Grouped chronologically from Today, Yesterday, to Older. Take 1-click action directly from cards: approve deliverables, request revisions with feedback notes, bookmark, or open the Task Drawer.',
        selector: '[data-tour="inbox-timeline-stream"]',
        placement: 'top',
    },
];
